import { useCallback, useState, useEffect } from 'react';
import { debounce, getValue } from '@umu/umu-util';
import { useMethodWhileMounted } from './useMethodWhileMounted';

interface IValidate<p> {
    name: string; // data.a.b.c 通过字符串路径去校验 value
    selector?: string; // 如果有 selector，则校验不通过时 querySelector 并滚动到该位置
    validateErr: (value: any, data: p) => string; // return error message, 如果没有则校验通过
}
type Validates<p> = Array<IValidate<p>>;
type Errors = { [name: string]: string };

/**
 * 监听数据变化并校验
 * @param data 需要监听的数据
 * @param validate 校验规则
 * @param continuously 是否自动监听数据变化并持续校验，默认 true
 * @returns pass 是否通过校验，errors 错误信息，doChect 开启校验, endCheck 结束校验
 */
export const useValidate = <p>(data: p, validate: Validates<p>, continuously = true) => {
    const [pass, setPass] = useState(false);
    const [validating, setValidating] = useState(false);
    const [errors, setErrors] = useState<Errors>({});

    const checkFnc = (): IValidate<p> | null => {
        let hasErr = false;
        const errorMap: Errors = {};
        let firstErr: IValidate<p> | null = null;
        validate.forEach((item) => {
            const { name, validateErr } = item;
            const value = getValue(data, name);
            const errTip = validateErr(value, data);
            if (errTip) {
                hasErr = true;
                errorMap[name] = errTip;
                firstErr = firstErr || item;
            }
        });
        setPass(!hasErr);
        setErrors(errorMap);
        return firstErr;
    };

    const checkHandle = useMethodWhileMounted(debounce(checkFnc, 500));

    useEffect(() => {
        if (validating) {
            checkHandle();
        }
    }, [data, validating]);

    const doChect = () => {
        if (continuously) {
            !validating && setValidating(true);
        }
        const firstErr = checkFnc();
        if (firstErr && firstErr.selector) {
            const dom = document.querySelector(firstErr.selector);
            if (dom) {
                dom.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
        return !firstErr;
    };

    const endCheck = useCallback(() => setValidating(false), []);

    return { pass, errors, validating, doChect, endCheck };
};
